import { usePagination } from "../../shared/lib/use-pagination.js";
import { Pagination } from "../../shared/ui/pagination.js";
import { useSearchParams } from "react-router-dom";

import type { components } from "../../shared/api/generated/trading-engine.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { useActivitySummary } from "../activities/queries.js";
import { backtestPnlGroup, pnlPeriodRange } from "../backtests/pnl-chart-models.js";
import { useBots } from "../bots/queries.js";
import { formatUsd } from "../positions/models.js";
import { useAccounts } from "../settings/queries.js";
import { useTradePnlChart, useAllTrades } from "./queries.js";
import { TradePnlChart } from "./trade-pnl-chart.js";

type ExecutedTrade = components["schemas"]["ExecutedTrade"];

function tradeAction(trade: Pick<ExecutedTrade, "side" | "positionEffect">): string {
  if (trade.side === "buy")
    return trade.positionEffect === "open" ? "Buy to open long" : "Buy to close short";
  return trade.positionEffect === "open" ? "Sell to open short" : "Sell to close long";
}

function price(value: string): string {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? new Intl.NumberFormat(undefined, {
        currency: "USD",
        maximumFractionDigits: 4,
        minimumFractionDigits: 2,
        style: "currency",
      }).format(parsed)
    : value;
}

function time(value: string): string {
  return Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString() : "Unavailable";
}

export function TradesPage() {
  const [search, setSearch] = useSearchParams();
  const accountId = search.get("account") ?? "";
  const botId = search.get("bot") ?? "";
  const symbol = search.get("symbol") ?? "";
  const from = search.get("from") ?? "";
  const to = search.get("to") ?? "";
  const cursor = search.get("cursor") ?? "";
  const view = search.get("view") === "chart" ? "chart" : "table";
  const groupBy = backtestPnlGroup(search.get("chartType"));
  const filters = {
    ...(accountId === "" ? {} : { brokerAccountId: accountId }),
    ...(botId === "" ? {} : { botId }),
    ...(symbol === "" ? {} : { symbol: symbol.toUpperCase() }),
    ...(from === "" ? {} : { from }),
    ...(to === "" ? {} : { to }),
  };
  const trades = useAllTrades(cursor, filters, view === "table");
  const chart = useTradePnlChart(filters, groupBy, view === "chart");
  const summary = useActivitySummary(filters);
  const pnlCoverage = summary.data ?? chart.data?.coverage;
  const accounts = useAccounts();
  const bots = useBots({});
  const accountById = new Map((accounts.data ?? []).map((account) => [account.id, account]));
  const botById = new Map((bots.data ?? []).map((bot) => [bot.id, bot]));
  const pagination = usePagination(trades.data?.items ?? []);
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "cursor") {
      next.delete("cursor");
      next.delete("page");
    }
    setSearch(next, { replace: true });
  };

  return (
    <section className="page-stack page-stack--compact" aria-label="Trade Activities">
      <div className="page-toolbar">
        <h1>Trade Activities</h1>
        <RefreshButton queries={[view === "chart" ? chart : trades, summary, accounts, bots]} />
      </div>
      <p className="page-intro">
        Broker-confirmed fills for orders placed by TradingApp Bots or its manual trading controls.
        Orders placed directly in Tastytrade are not included.
      </p>
      <div className="content-card activity-summary" aria-label="Live trade outcomes">
        <div>
          <span>Closed trades</span>
          <strong>{summary.data?.totalTrades ?? "—"}</strong>
          {summary.data === undefined ? null : (
            <small>
              {summary.data.winningTrades} wins · {summary.data.losingTrades} losses
            </small>
          )}
        </div>
        <div>
          <span>Win rate</span>
          <strong>
            {summary.data === undefined ? "—" : `${summary.data.winRate.toFixed(2)}%`}
          </strong>
        </div>
        <div>
          <span>Realized P/L</span>
          <strong>
            {summary.data === undefined ? "—" : formatUsd(summary.data.realizedPnlUsd)}
          </strong>
          <small>Gross, before fees · closed shares with recorded opening basis</small>
        </div>
        <div>
          <span>Range</span>
          <strong>
            {from === "" && to === "" ? "All time" : `${from || "Start"} → ${to || "Now"}`}
          </strong>
        </div>
      </div>
      {summary.error === null ? null : (
        <ResourceState
          kind="degraded"
          title="Trade summary unavailable"
          description="The filled-order ledger remains available."
        />
      )}
      {pnlCoverage !== undefined && pnlCoverage.unmatchedClosingFills > 0 ? (
        <div className="truth-banner truth-banner--local-projection" role="status">
          <strong>Realized P/L coverage is incomplete</strong>
          <span>
            {pnlCoverage.unmatchedClosingShares} closing shares in{" "}
            {pnlCoverage.unmatchedClosingFills} fills have no TradingApp-recorded opening basis and
            are excluded from P/L.
          </span>
        </div>
      ) : null}
      <div className="backtest-view-toolbar">
        <div className="tab-list" role="tablist" aria-label="Trade Activities view">
          <button
            aria-selected={view === "table"}
            onClick={() => setFilter("view", "table")}
            role="tab"
            type="button"
          >
            Table
          </button>
          <button
            aria-selected={view === "chart"}
            onClick={() => setFilter("view", "chart")}
            role="tab"
            type="button"
          >
            Chart
          </button>
        </div>
      </div>
      <div className="content-card trade-filters" aria-label="Trade filters">
        <div className="field-stack">
          <label htmlFor="trade-account">Account</label>
          <select
            id="trade-account"
            value={accountId}
            onChange={(event) => setFilter("account", event.currentTarget.value)}
          >
            <option value="">All accounts</option>
            {(accounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="trade-bot">Bot</label>
          <select
            id="trade-bot"
            value={botId}
            onChange={(event) => setFilter("bot", event.currentTarget.value)}
          >
            <option value="">All Bots and manual orders</option>
            {(bots.data ?? []).map((bot) => (
              <option key={bot.id} value={bot.id}>
                {bot.name} · {bot.symbol}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="trade-symbol">Symbol</label>
          <input
            id="trade-symbol"
            value={symbol}
            onChange={(event) => setFilter("symbol", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="trade-from">From</label>
          <input
            id="trade-from"
            type="date"
            value={from}
            onChange={(event) => setFilter("from", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="trade-to">To</label>
          <input
            id="trade-to"
            type="date"
            value={to}
            onChange={(event) => setFilter("to", event.currentTarget.value)}
          />
        </div>
      </div>
      {view === "chart" ? (
        <div
          className="content-card section-stack backtest-chart-card"
          aria-label="Trade P/L chart"
        >
          <div className="connection-heading">
            <div>
              <h2>Realized P/L</h2>
              <p className="section-description">
                TradingApp-recorded closing fills · gross, before fees · New York trading dates
              </p>
            </div>
            <div className="field-stack">
              <label htmlFor="trade-chart-group">Group by</label>
              <select
                id="trade-chart-group"
                value={groupBy}
                onChange={(event) => setFilter("chartType", event.currentTarget.value)}
              >
                <option value="date">Date</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="symbol">Symbol</option>
              </select>
            </div>
          </div>
          {chart.isLoading ? (
            <ResourceState
              kind="loading"
              title="Loading P/L chart"
              description="Aggregating all matching closing fills."
            />
          ) : chart.error !== null || chart.data === undefined ? (
            <ResourceState
              kind="error"
              title="P/L chart unavailable"
              description="TradingEngine could not aggregate closing fills."
            />
          ) : chart.data.items.length === 0 ? (
            <ResourceState
              kind="empty"
              title="No realized outcomes"
              description="No matching closing fills have a TradingApp-recorded opening basis."
            />
          ) : (
            <TradePnlChart
              groupBy={groupBy}
              items={chart.data.items}
              onSelect={(item) => {
                if (groupBy === "symbol") {
                  window.open(
                    `/tickers/${encodeURIComponent(item.key)}?view=live`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                  return;
                }
                const range = pnlPeriodRange(groupBy, item.key);
                const next = new URLSearchParams(search);
                next.set("from", range.pnlFrom);
                next.set("to", range.pnlTo);
                next.delete("cursor");
                next.delete("page");
                setSearch(next, { replace: true });
              }}
            />
          )}
        </div>
      ) : null}
      {view === "table" && trades.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading filled orders"
          description="Reading broker-confirmed executions from TradingEngine."
        />
      ) : null}
      {view !== "table" || trades.error === null ? null : (
        <ResourceState
          kind="error"
          title="Trade Activities unavailable"
          description="TradingEngine could not return filled orders. History remains available for diagnostics."
        />
      )}
      {view === "table" &&
      !trades.isLoading &&
      trades.error === null &&
      trades.data?.items.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No filled orders"
          description="No TradingApp order with broker-confirmed fills matches these filters."
        />
      ) : null}
      {view === "table" && (trades.data?.items.length ?? 0) > 0 ? (
        <div className="content-card trade-table-scroll">
          <table className="trade-table">
            <thead>
              <tr>
                <th scope="col">Symbol</th>
                <th scope="col">Action</th>
                <th scope="col">Status</th>
                <th scope="col">Filled shares</th>
                <th scope="col">Avg. fill price</th>
                <th scope="col">Last fill</th>
                <th scope="col">Account</th>
                <th scope="col">Source</th>
                <th scope="col">Executions</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagination.items.map((trade) => (
                <tr key={trade.id}>
                  <td>
                    <strong>{trade.symbol}</strong>
                  </td>
                  <td>{tradeAction(trade)}</td>
                  <td>
                    <span className={`status-pill status-pill--${trade.status}`}>
                      {trade.status}
                    </span>
                  </td>
                  <td>
                    {trade.filledQuantity}
                    {trade.filledQuantity === trade.requestedQuantity
                      ? ""
                      : ` of ${trade.requestedQuantity}`}
                  </td>
                  <td>{price(trade.averageFillPriceUsd)}</td>
                  <td>
                    <time dateTime={trade.filledAt}>{time(trade.filledAt)}</time>
                  </td>
                  <td>
                    {accountById.get(trade.brokerAccountId)?.displayName ?? trade.brokerAccountId}
                  </td>
                  <td>
                    {trade.origin === "manual"
                      ? "Manual"
                      : trade.botId === undefined
                        ? "Bot"
                        : (botById.get(trade.botId)?.name ?? `Bot ${trade.botId}`)}
                  </td>
                  <td>
                    <details className="trade-executions">
                      <summary>
                        {trade.fills.length} {trade.fills.length === 1 ? "fill" : "fills"}
                      </summary>
                      <p>
                        Order {trade.id}
                        {trade.providerOrderId === undefined
                          ? ""
                          : ` · Broker ${trade.providerOrderId}`}
                      </p>
                      <p>
                        {trade.orderType} · {trade.timeInForce}
                      </p>
                      <ol>
                        {trade.fills.map((fill) => (
                          <li key={fill.id}>
                            {fill.quantity} shares @ {price(fill.priceUsd)} ·{" "}
                            {time(fill.occurredAt)}
                          </li>
                        ))}
                      </ol>
                    </details>
                  </td>
                  <td>
                    <details className="trade-row-menu">
                      <summary aria-label={`Actions for order ${trade.id}`}>⋯</summary>
                      <div className="trade-row-menu__items">
                        <a
                          href={`/tickers/${encodeURIComponent(trade.symbol)}?view=live&orderId=${encodeURIComponent(trade.id)}`}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          Open Chart
                        </a>
                      </div>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {view === "table" ? (
        <Pagination {...pagination} disabled={trades.isFetching || trades.error !== null} />
      ) : null}
    </section>
  );
}
